import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import { ICityPageStatistics } from "../../../interfaces/endpoints/ICityPage";
import {
  getCityPageStatistics, updateCityPageStatistics
} from "../../../services/CityPages.service";
import FormWrapper from "../../ui/FormWrapper";
import Input from "../../ui/input/Input";
import SpinnerLoader from "../../ui/SpinnerLoader";

const defaults: ICityPageStatistics = {
  cityPageId: 0, averageTripDuration: 0, averageTicketPrice: 0,
  minTicketPrice: 0, maxTicketPrice: 0, earliestBusDeparture: "",
  latestBusDeparture: "", dailyBuses: 0, operatorIds: "",
  hasReservable: false, reservationCount: 0, resultCount: 0,
  totalLinePages: 0
};

const EditCityPageStatistics = () => {
  const { id } = useParams<{ id: string }>();
  const methods = useForm<ICityPageStatistics>({ defaultValues: defaults });
  const { reset, handleSubmit, formState } = methods;

  const { response, loading, refetch } = useFetch(
    () => getCityPageStatistics(Number(id)),
    { initRequest: true }
  );

  useEffect(() => {
    if (response?.data) reset(response.data);
  }, [response, reset]);

  const onSubmit: SubmitHandler<ICityPageStatistics> = async (data) => {
    try {
      await updateCityPageStatistics(Number(id), data);
      toast.success("Statistikat u përditësuan me sukses.", { position: "top-right" });
      refetch();
    } catch {
      toast.error("Ndodhi një gabim gjatë përditësimit.", { position: "top-right" });
    }
  };

  const gridFields: { name: keyof ICityPageStatistics; label: string; type?: string }[] = [
    { name: "averageTripDuration", label: "Kohëzgjatja mesatare (min)", type: "number" },
    { name: "dailyBuses", label: "Autobusë ditorë", type: "number" },
    { name: "averageTicketPrice", label: "Çmimi mesatar (EUR)", type: "number" },
    { name: "minTicketPrice", label: "Çmimi minimal (EUR)", type: "number" },
    { name: "maxTicketPrice", label: "Çmimi maksimal (EUR)", type: "number" },
    { name: "totalLinePages", label: "Gjithsej faqe linjash", type: "number" },
    { name: "earliestBusDeparture", label: "Nisja më e hershme (p.sh. 06:00)" },
    { name: "latestBusDeparture", label: "Nisja më e vonshme (p.sh. 22:00)" }
  ];
  const fullWidthFields: { name: keyof ICityPageStatistics; label: string; type?: string }[] = [
    { name: "operatorIds", label: "ID-të e operatorëve (ndarë me presje)" }
  ];

  return (
    <FormProvider {...methods}>
      <FormWrapper
        onSubmit={handleSubmit(onSubmit)}
        title="Modifiko Statistikat e Qytetit"
        showPrompt={
          !formState.isSubmitSuccessful &&
          !!Object.keys(formState.dirtyFields).length
        }
      >
        <div className="min-h-[500px] flex items-center justify-center">
          {loading ? (
            <SpinnerLoader className="text-primary" />
          ) : (
            <div className="w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gridFields.map((f) => (
                  <Input
                    key={f.name}
                    type={f.type}
                    {...methods.register(f.name)}
                    label={f.label}
                    placeholder={`Shkruaj ${f.label.toLowerCase()}...`}
                    error={formState.errors[f.name]?.message}
                  />
                ))}
              </div>
              {fullWidthFields.map((f) => (
                <Input
                  key={f.name}
                  type={f.type}
                  {...methods.register(f.name)}
                  label={f.label}
                  className="mt-4"
                  placeholder={`Shkruaj ${f.label.toLowerCase()}...`}
                  error={formState.errors[f.name]?.message}
                />
              ))}
            </div>
          )}
        </div>
      </FormWrapper>
    </FormProvider>
  );
};

export default EditCityPageStatistics;
